import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, RefreshCw, Ban, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const STATUS_STYLE = {
  pending:          { label: "Pending",        cls: "bg-zinc-100 text-zinc-700" },
  created:          { label: "Created",        cls: "bg-zinc-100 text-zinc-700" },
  sent:             { label: "Sent",           cls: "bg-sky-100 text-sky-700" },
  delivered:        { label: "Delivered",      cls: "bg-emerald-100 text-emerald-700" },
  accepted:         { label: "Accepted",       cls: "bg-emerald-100 text-emerald-700" },
  expired:          { label: "Expired",        cls: "bg-amber-100 text-amber-800" },
  revoked:          { label: "Revoked",        cls: "bg-zinc-200 text-zinc-500 line-through" },
  failed:           { label: "Failed",         cls: "bg-red-100 text-red-700" },
  bounced:          { label: "Bounced",        cls: "bg-red-100 text-red-700" },
  complained:       { label: "Complained",     cls: "bg-red-100 text-red-700" },
  delivery_failed:  { label: "Failed",         cls: "bg-red-100 text-red-700" },
};

/**
 * List / manage invitations. Owner + Admin scoped.
 * Actions: resend, revoke, delete (for expired/revoked/accepted), copy link.
 */
export default function InvitationsList() {
  const [rows, setRows] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [linkFor, setLinkFor] = useState(null); // { id, token, url, expires_at }
  const load = async () => {
    try {
      const { data } = await api.get("/invitations");
      setRows(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[invites] load failed", err);
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = (rows || []).filter((r) => filter === "all" ? true : r.computed_status === filter);

  const resend = async (r) => {
    try {
      const { data } = await api.post(`/invitations/${r.id}/resend`);
      setLinkFor(data);
      const st = data.delivery_status;
      if (st === "sent") toast.success("Invitation resent.");
      else if (st === "failed") toast.warning("Email delivery failed. Copy the new link instead.");
      else toast.info("New link generated. Copy it below — email delivery is not configured.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend.");
    }
  };
  const revoke = async (r) => {
    if (!window.confirm(`Revoke invitation for ${r.invited_email || r.email}?`)) return;
    try { await api.post(`/invitations/${r.id}/revoke`); toast.success("Revoked."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not revoke."); }
  };
  const remove = async (r) => {
    if (!window.confirm("Remove this invitation record?")) return;
    try { await api.delete(`/invitations/${r.id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove."); }
  };
  const copyLink = async (url, id) => {
    try { await navigator.clipboard.writeText(url); }
    catch (err) {
      if (process.env.NODE_ENV === "development") console.debug("[invites-list] clipboard blocked", err);
    }
    setCopiedId(id);
    track("invitation_link_copied");
    toast.success("Link copied.");
    setTimeout(() => setCopiedId(null), 1200);
  };

  if (rows === null) return <div className="text-sm text-zinc-500 py-4">Loading…</div>;

  return (
    <div data-testid="invitations-list">
      <div className="flex items-center gap-2 mb-3 overflow-x-auto scroll-thin">
        {["all", "pending", "sent", "accepted", "failed", "expired", "revoked"].map((k) => (
          <button
            key={k}
            data-testid={`invites-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition ${
              filter === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
            }`}
          >
            {k}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {linkFor && (
        <div className="rounded-lg border p-3 mb-4 bg-zinc-50" data-testid="invites-fresh-link">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Fresh link (previous link is now invalid)</div>
          <div className="flex items-center gap-2">
            <input readOnly value={linkFor.invite_url || linkFor.token || ""}
                   className="flex-1 text-xs border rounded px-2 py-1.5" />
            <Button size="sm" onClick={() => copyLink(linkFor.invite_url, "fresh")} className="bg-[var(--brand-primary)] text-white">
              {copiedId === "fresh" ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
            </Button>
            <button className="text-xs text-zinc-500 hover:text-zinc-900" onClick={() => setLinkFor(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-10 border rounded-lg bg-zinc-50/40" data-testid="invites-empty-state">
          <div className="text-sm text-zinc-500">No invitations {filter !== "all" ? `with status "${filter.replace(/_/g," ")}"` : "yet"}.</div>
        </div>
      ) : (
        <div className="border rounded-lg divide-y">
          {filtered.map((r) => (
            <InvitationRow
              key={r.id} r={r}
              onResend={() => resend(r)}
              onRevoke={() => revoke(r)}
              onRemove={() => remove(r)}
              onCopy={() => r.token && copyLink(r.invite_url, r.id)}
              copied={copiedId === r.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InvitationRow({ r, onResend, onRevoke, onRemove, onCopy, copied }) {
  const st = STATUS_STYLE[r.computed_status] || STATUS_STYLE.pending;
  const email = r.invited_email || r.email;
  const role = r.invited_role || r.role;
  const isTerminal = ["accepted", "revoked", "expired"].includes(r.computed_status);
  return (
    <div className="p-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-3" data-testid={`invite-row-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{email}</div>
        <div className="text-[11px] text-zinc-500 truncate">
          {role} · sent {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : "—"} · expires {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"} · by {r.invited_by_name || "—"}
        </div>
      </div>
      <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
      <div className="flex items-center gap-1">
        {!isTerminal && (
          <>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onResend} data-testid={`invite-resend-${r.id}`}>
              <RefreshCw className="w-3 h-3 mr-1"/> Resend
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onRevoke} data-testid={`invite-revoke-${r.id}`}>
              <Ban className="w-3 h-3 mr-1"/> Revoke
            </Button>
          </>
        )}
        {isTerminal && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onRemove} data-testid={`invite-remove-${r.id}`}>
            <Trash2 className="w-3 h-3 mr-1"/> Remove
          </Button>
        )}
      </div>
    </div>
  );
}
